import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CellDefault,
  formatBalance,
  formatPercentage,
  getFirstWord,
  imageFromPlatformName,
  StepTable,
  TagInfo,
  TokenTag,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import type { MarginAccount, MarginPlatforms } from "../../../types/margin";
import { getTokenBySymbol } from "../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { NumberLabel, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

interface MarginBalancesProps {
  selectedAccounts: MarginAccount[];
}

interface MarginBalance {
  mint: string;
  depositApy: number;
  borrowApy: number;
  net: number;
  inOrders: number;
  symbol: string;
  logoURI?: string;
  kind: MarginPlatforms;
  deposits: number;
  borrows: number;
}
export const MarginBalances: FC<MarginBalancesProps> = ({ selectedAccounts }) => {
  const { priceInUSD } = useMarkets();
  const totals = useMemo(() => {
    return selectedAccounts.reduce<{ [key: string]: MarginBalance }>((data, account) => {
      account.balances.forEach((balance) => {
        const { deposit, borrow, net, inOrders, borrowApy, depositApy, mint, symbol, logoURI } = balance;
        // using a compound key to differentiate separate platforms with same mint
        const key = `${account.kind}-${mint}`;
        if (data[key]) {
          const existingPosition = data[key];
          const newPosition = {
            deposits: existingPosition.deposits + deposit,
            borrows: existingPosition.borrows + borrow,
            net: existingPosition.net + net,
            inOrders: existingPosition.inOrders + inOrders,
          };
          data[key] = {
            ...existingPosition,
            ...newPosition,
          };
          return;
        } else {
          data[key] = {
            mint,
            depositApy,
            borrowApy,
            net,
            inOrders,
            symbol,
            logoURI,
            kind: account.kind,
            deposits: deposit,
            borrows: borrow,
          };
        }
      });
      return data;
    }, {});
  }, [selectedAccounts]);

  const columnData = useMemo(() => Object.values(totals), [totals]);

  const totalBalanceValue = useMemo(() => {
    return columnData.reduce((total, currentValue) => {
      const price = priceInUSD(currentValue.mint) ?? 0;
      const totalEquity = price * currentValue.net;
      return total + totalEquity;
    }, 0);
  }, [columnData, priceInUSD]);

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "marginBalancesPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            marginTrading: { marginBalances: marginBalancesText },
          },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(columnData.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [columnData.length, paginationSetting]);

  const balanceTableColumns: StepColumnsType<MarginBalance> = [
    {
      title: common.asset,
      key: "kind",
      dataIndex: "kind",
      render(_, record) {
        return (
          <AssetContainer>
            <TokenContainer>
              <TokenIcon mintAddress={record.mint} />
              <TokenIconText bold>{record.symbol}</TokenIconText>
            </TokenContainer>
            <TokenTag
              icon={<URLIcon size={16} src={imageFromPlatformName(record.kind) ?? ""} />}
              text={getFirstWord(record.kind) ?? ""}
              uppercase={false}
            />
          </AssetContainer>
        );
      },
    },
    {
      title: marginBalancesText.deposits,
      key: "deposits",
      dataIndex: "deposits",
      className: "column-number",
      sorter: (a, b) => a.deposits - b.deposits,
      render(_, { symbol, deposits, depositApy }) {
        const tokenInfo = getTokenBySymbol(symbol);
        const hasBalance = deposits > 0;
        return (
          <>
            {hasBalance ? (
              <CellValue cellType="number">{formatBalance(deposits, tokenInfo?.decimals)}</CellValue>
            ) : (
              <CellDefault />
            )}
            {hasBalance && (
              <TagInfoContainer>
                <TagInfo tagType="performance" performanceNumber={depositApy / 100}>
                  <NumberLabel>{formatPercentage(depositApy / 100, { performance: true })}</NumberLabel>
                </TagInfo>
              </TagInfoContainer>
            )}
          </>
        );
      },
    },
    {
      title: marginBalancesText.borrows,
      key: "borrows",
      className: "column-number",
      dataIndex: "borrows",
      sorter: (a, b) => a.borrows - b.borrows,
      render(_, { borrowApy, symbol, borrows }) {
        const tokenInfo = getTokenBySymbol(symbol);
        const hasBalance = borrows !== 0;
        return (
          <>
            {hasBalance ? (
              <CellValue cellType="number">{formatBalance(borrows, tokenInfo?.decimals)}</CellValue>
            ) : (
              <CellDefault />
            )}
            {hasBalance && (
              <TagInfoContainer>
                <TagInfo tagType="performance" performanceNumber={borrowApy * -1}>
                  <NumberLabel>{formatPercentage((borrowApy * -1) / 100, { performance: true })}</NumberLabel>
                </TagInfo>
              </TagInfoContainer>
            )}
          </>
        );
      },
    },
    {
      title: marginBalancesText.inOrders,
      key: "inOrders",
      dataIndex: "inOrders",
      className: "column-number",
      sorter: (a, b) => a.inOrders - b.inOrders,
      render(_, record) {
        return <CellValue cellType="value">{record.inOrders}</CellValue>;
      },
    },
    {
      title: marginBalancesText.netPositionValue,
      key: "net",
      dataIndex: "net",
      className: "column-number",
      defaultSortOrder: "descend",
      sorter: (a, b) => (priceInUSD(a.mint) ?? 0) * a.net - (priceInUSD(b.mint) ?? 0) * b.net,
      render(net: number, { depositApy, deposits, borrows, borrowApy, mint }) {
        const price = priceInUSD(mint);

        if (price === null) {
          return <CellDefault />;
        }
        const depositValue = price * deposits;
        const borrowValue = price * borrows * -1;
        const totalEquity = price * net;
        let netApy: number;

        if (deposits !== 0 && borrows !== 0) {
          netApy = (depositValue * depositApy + borrowValue * borrowApy) / Math.abs(totalEquity);
        } else if (deposits === 0) {
          netApy = borrowApy * -1;
        } else {
          netApy = depositApy;
        }

        const shouldRenderTag = deposits !== 0 || borrows !== 0;
        return (
          <>
            <CellValue cellType="userPositionValue" fontWeight="bold">
              {totalEquity}
            </CellValue>
            {shouldRenderTag && (
              <TagInfoContainer>
                <TagInfo tagType="performance" performanceNumber={netApy / 100}>
                  <NumberLabel>{formatPercentage(netApy / 100, { performance: true })}</NumberLabel>
                </TagInfo>
              </TagInfoContainer>
            )}
          </>
        );
      },
    },
  ];
  return (
    <YourPositionContainer>
      <ChevronCollapse isCollapseChild title={marginBalancesText.title} value={totalBalanceValue}>
        <StepTable
          rowKey={({ kind, mint }) => kind + mint}
          pagination={pagination}
          onChange={(pagination) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
          }}
          columns={balanceTableColumns}
          key="margin-position-table"
          dataSource={columnData}
        />
      </ChevronCollapse>
    </YourPositionContainer>
  );
};

const YourPositionContainer = styled.div`
  margin-top: 20px;
`;

const TagInfoContainer = styled.div`
  display: inline-block;
  margin-left: 10px;
`;

const AssetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TokenContainer = styled.div`
  display: flex;
  margin-right: 16px;
`;
